import * as React from 'react';
import PrismicDOM from 'prismic-dom';
import { deps, inject, observer, StatefulComponent } from '../../lib/component';
import { classes, style } from 'typestyle';
import { ResponsiveImage } from '../../components/responsive-image';
import {
  colorBackground,
  colorBlack,
  colorBrand,
  colorContrast,
  colorGold,
  colorSlate,
  colorSubtle,
  colorWhite,
  colorYouTube,
} from '../../theme/color';
import { em, important, percent, px, rem, viewHeight, viewWidth } from 'csx';
import {
  center,
  centerCenter,
  endJustified,
  horizontal,
  horizontallySpaced,
  startJustified,
  vertical,
  verticallySpaced,
} from 'csstips';
import { mediaMobileOnly, mediaTablet, mediaTabletLandscape } from '../../theme/media';
import { ButtonPill } from '../../components/button-pill';
import FullWidthText from '../../components/full-width-text';
import { REFERRER_CHANNEL_KEY } from '../../constants';
import { graphql, InjectedGraphQLProps } from 'react-apollo';
import gql from 'graphql-tag';
import maybe from '../../lib/maybe';
import { Modal, ModalChildStyle } from '../../components/modal';
import { YoutubeChannel } from '../../types/graphql';
import { ImageTile } from '../image-tile';
import { Logo } from '../logo';
import GoogleSignIn from '../user/google-sign-in';
import { SVGIcon } from '../svg-icon';
import { constantGoldenRatio } from '../../theme/constant';
import { CopyBox } from '../copy-box';
import { TextNoOrphan } from '../text-no-orphan';
import { Loading } from '../loading/loading';
import { ChannelSchema } from '../../types/schema';
import { replaceValues } from '../../lib/helpers';
import getCurrentDomain from '../../lib/getCurrentDomain';
import Helmet from 'react-helmet';

type Props = {
  channel: string;
};

type State = {
  accept: boolean;
};

type Data = {
  channel: YoutubeChannel | null;
};

type CombinedProps = Props & InjectedGraphQLProps<Data>;

@inject(deps)
@observer
class ChannelReferral extends StatefulComponent<CombinedProps, State> {
  state = {
    accept: false,
  };

  render() {
    const { data } = this.props;
    const { accept } = this.state;
    const { handleClickLink } = this.props.controller.ui;
    const { preloadContent } = this.props.controller.image;
    const { user, channel, referralURL, loading, loadingChannels } = this.props.model.user;

    const model = this.props.model.page.channelReferral;

    const landingChannel = maybe(() => channel || data.channel!, null);

    const image = ChannelReferral.getChannelImage(landingChannel);

    const __html = landingChannel
      ? PrismicDOM.RichText.asHtml(
          model.modal_copy_match.map((r) => ({
            ...r,
            text: replaceValues(r.text, { channel: landingChannel.name }),
          })),
        )
      : PrismicDOM.RichText.asHtml(model.modal_copy_missing);

    return (
      <ResponsiveImage
        asBackground={true}
        preload={preloadContent}
        className={ChannelReferral.styles.background}
        identity={model.background_image}
      >
        <Helmet>
          <meta name="robots" content="noindex" />
        </Helmet>
        {!!image && !loading && !loadingChannels && (
          <div
            className={classes(
              ChannelReferral.styles.visual,
              channel ? ChannelReferral.styles.visualUser : ChannelReferral.styles.visualReferrer,
            )}
          >
            <ImageTile src={image} />
          </div>
        )}
        <div className={classes(ChannelReferral.styles.content, !image && ChannelReferral.styles.contentMissing)}>
          {!user && (
            <h1 className={ChannelReferral.styles.header}>
              {!!landingChannel && <FullWidthText>{landingChannel.name}</FullWidthText>}
              {!landingChannel && model.header_missing}
            </h1>
          )}
          {!!user && (
            <h2 className={ChannelReferral.styles.subheader}>
              <TextNoOrphan value={model.sub_header} />
            </h2>
          )}
          {!!landingChannel && !user && (
            <p className={ChannelReferral.styles.text}>
              {model.cta_invite_copy} <Logo className={ChannelReferral.styles.logo} width={42} />
            </p>
          )}
          {!landingChannel && !user && <p className={ChannelReferral.styles.copy}>{model.cta_missing_copy}</p>}
          {!!user && (loading || loadingChannels) && (
            <p className={ChannelReferral.styles.text}>
              <Loading light={true} />
            </p>
          )}
          {!!user && !!referralURL && <p className={ChannelReferral.styles.copy}>{model.referral_text}</p>}
          {!!user && !!referralURL && <CopyBox className={ChannelReferral.styles.copyBox} content={referralURL} />}
          {!user && (
            <ButtonPill className={ChannelReferral.styles.button} bouncing={true} onClick={this.handleSignUp}>
              {model.cta_landing_button}
            </ButtonPill>
          )}
          {!!user && (
            <ButtonPill
              className={ChannelReferral.styles.button}
              bouncing={true}
              onClick={handleClickLink}
              primary={true}
              href={model.cta_complete_url}
            >
              {model.cta_complete_button}
            </ButtonPill>
          )}
        </div>
        {!user && (
          <Modal show={accept} onRequestClose={this.closeSignUp}>
            <div className={ChannelReferral.styles.modal}>
              {!!image && (
                <div className={ChannelReferral.styles.modalSplit}>
                  <ImageTile src={image} />
                  <div>
                    {model.modal_hero_pre}{' '}
                    <Logo className={ChannelReferral.styles.logo} color={colorSlate} width={42} />{' '}
                    {model.modal_hero_post}
                  </div>
                </div>
              )}
              <h3>{model.modal_title}</h3>
              <div className={ChannelReferral.styles.contentCopy} dangerouslySetInnerHTML={{ __html }} />
              <GoogleSignIn />
              <div className={ChannelReferral.styles.mandate}>{model.modal_caveat}</div>
              <div className={ChannelReferral.styles.legals}>
                <a onClick={this.handleClickTermsConditions} href={'/terms-and-conditions'}>
                  {ChannelReferral.i18n.termsConditions}
                </a>
                <a onClick={this.handleClickPrivacyPolicy} href={'/privacy-policy'}>
                  {ChannelReferral.i18n.privacyPolicy}
                </a>
              </div>
              <div className={ChannelReferral.styles.authority}>
                <Logo color={colorSlate} />
                <SVGIcon.YouTubeCertified size={32} />
              </div>
            </div>
          </Modal>
        )}
      </ResponsiveImage>
    );
  }

  handleSignUp = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.setState({ accept: true });
  };

  closeSignUp = () => this.setState({ accept: false });

  handleClickTermsConditions = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.onboarding.showTermsConditionsModal();
  };

  handleClickPrivacyPolicy = (event: React.MouseEvent<any>) => {
    event.preventDefault();
    this.props.controller.onboarding.showPrivacyPolicyModal();
  };

  componentWillMount() {
    this.props.controller.ui.setChrome(false);

    this.setChannelMeta();
    this.setChannelCookie();
  }

  componentDidMount() {
    this.sendVisit();
  }

  componentDidUpdate(props: CombinedProps) {
    if (this.props.data !== props.data) {
      this.setChannelMeta();
      this.setChannelCookie();
      this.sendVisit();
    }
  }

  componentWillUnmount() {
    this.props.controller.ui.setChrome(true);
    this.props.controller.ui.setOpenGraph();
  }

  private setChannelMeta = () => {
    const channel = this.getChannel();
    const model = this.props.model.page.channelReferral;
    const title = channel
      ? replaceValues(model.document_title_match, { channel: channel.name })
      : model.document_title_missing;

    this.props.controller.ui.setTitle(title);
    this.props.controller.ui.setDescription(model.document_description);
  };

  private setChannelCookie = () => {
    const channel = this.getChannel();

    if (channel && channel.id) {
      const domain = '.' + getCurrentDomain();

      document.cookie = `${REFERRER_CHANNEL_KEY}=${channel.id};domain=${domain};path=/`;
    }
  };

  private sendVisit = () => {
    const channel = this.getChannel();

    if (!channel) return;

    const visitKey = 'hasSentChannelVisit:' + channel.id;
    if (window.sessionStorage.getItem(visitKey)) return;

    this.props.controller.analytics.sendChannelReferralVisit(channel);
    window.sessionStorage.setItem(visitKey, '1');
  };

  private getChannel = () => maybe(() => this.props.data.channel!, null);

  static getChannelImage = (channel: YoutubeChannel | ChannelSchema | null): string | null => {
    if (!channel || !channel.images || !channel.images.length) return null;

    const image = channel.images.find((i) => i.size === 'high');

    return image ? image.url : channel.images[0].url;
  };

  static i18n = {
    termsConditions: 'Terms & Conditions',
    privacyPolicy: 'Privacy Policy',
  };

  static styles = {
    background: style(
      {
        ...vertical,
        ...center,
        ...startJustified,
        position: 'fixed',
        top: 0,
        right: 0,
        left: 0,
        minHeight: rem(32),
        bottom: 0,
        padding: 0,
        overflow: 'hidden',
        backgroundColor: colorBlack.toString(),
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundRepeat: 'no-repeat',
      },
      mediaMobileOnly({
        padding: rem(2),
      }),
      mediaTablet({
        padding: rem(5),
      }),
    ),
    modal: style({
      ...ModalChildStyle,
      ...center,
      width: px(420),
      maxWidth: 'calc(100vw - 4rem)',
      background: colorBackground.lighten(0.05).toString(),
      color: colorSlate.toString(),
      position: 'relative',
      $nest: {
        '& p': {
          zIndex: 42,
          margin: '0 0 1.5em',
        },
        '& strong': {
          color: colorContrast.toString(),
          fontWeight: 600,
        },
        '& h3': {
          zIndex: 42,
          textAlign: 'left',
          width: percent(100),
          color: colorBrand.toString(),
          margin: '0 0 2rem 0',
          fontSize: rem(2),
        },
      },
    }),
    modalSplit: style({
      ...centerCenter,
      ...horizontal,
      width: percent(100 / constantGoldenRatio),
      margin: '0 0 2rem 0',
      $nest: {
        '&>*': {
          flex: 1,
        },
        '&>div:first-child': {
          borderRadius: percent(50),
          width: percent(100),
          overflow: 'hidden',
          marginRight: rem(0.5),
        },
        '&>div:last-child': {
          ...horizontal,
          ...center,
          fontSize: rem(1.25),
        },
        '& svg': {
          margin: '0 0.5rem',
          height: rem(1.875),
          width: 'auto',
        },
      },
    }),
    modalCover: style({
      borderRadius: rem(1),
      position: 'absolute',
      top: 0,
      right: 0,
      left: 0,
      bottom: 0,
    }),
    modalCoverGradient: style({
      background: `radial-gradient(
  circle at 94% 10%,
  ${colorGold.fade(0.12).toString()}, 
  ${colorBlack.fade(0.35).toString()} 68%,
  ${colorGold.darken(0.05).fade(0.05).toString()}
)`,
    }),
    logo: style({
      flexShrink: 0,
    }),
    header: style(
      {
        ...vertical,
        ...center,
        ...endJustified,
        margin: 0,
        color: colorWhite.toString(),
        textAlign: 'center',
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: viewWidth(11),
      }),
      mediaTablet({
        width: px(570),
        fontSize: rem(5),
      }),
      mediaTabletLandscape({
        fontSize: important('7vh'),
      }),
    ),
    subheader: style(
      {
        ...vertical,
        ...center,
        ...endJustified,
        margin: 0,
        color: colorWhite.toString(),
        textAlign: 'center',
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: viewWidth(11),
      }),
      mediaTablet({
        width: px(570),
        fontSize: rem(3.75),
      }),
    ),
    visual: style(
      {
        ...centerCenter,
        flex: 2,
        width: percent(100),
        margin: '1rem 0 2rem',
        $nest: {
          '&>div': {
            borderRadius: percent(50),
            width: percent(100),
            overflow: 'hidden',
          },
        },
      },
      mediaMobileOnly({
        margin: '1rem 0 2rem',
      }),
      mediaTablet({
        margin: '1rem 0 4rem',
      }),
    ),
    visualReferrer: style(
      mediaMobileOnly({
        maxWidth: px(400),
      }),
      mediaTablet({
        maxWidth: viewHeight(40),
      }),
    ),
    visualUser: style(
      mediaMobileOnly({
        width: viewWidth(100 / constantGoldenRatio),
        maxWidth: viewHeight(100 - 100 / constantGoldenRatio),
      }),
      mediaTablet({
        maxWidth: viewHeight(100 - 100 / constantGoldenRatio),
      }),
      mediaTabletLandscape({
        maxWidth: `${viewHeight(100 / constantGoldenRatio / constantGoldenRatio / constantGoldenRatio)} !important`,
      }),
    ),
    copyBox: style({
      maxWidth: percent(100),
      margin: '0 0 2rem 0',
    }),
    content: style({
      ...vertical,
      ...center,
      ...startJustified,
      flexShrink: 0,
      maxWidth: percent(100),
    }),
    contentCopy: style({
      zIndex: 42,
    }),
    contentMissing: style({
      flex: 1,
      justifyContent: important('center'),
    }),
    copy: style(
      {
        textAlign: 'center',
        color: colorWhite.toString(),
        lineHeight: em(1.375),
        maxWidth: percent(100),
        fontSize: rem(1),
        margin: '1rem 0 2rem 0',
        flexShrink: 0,
      },
      mediaMobileOnly({
        width: percent(100),
      }),
      mediaTablet({
        width: viewWidth(69),
      }),
    ),
    button: style(
      {
        transition: 'margin-top 0.150s ease-in',
      },
      mediaMobileOnly({
        fontSize: rem(1),
      }),
      mediaTablet({
        fontSize: rem(1.25),
      }),
      mediaTabletLandscape({
        fontSize: important('1.25rem'),
      }),
    ),
    buttonContainer: style({
      flexShrink: 0,
      paddingTop: rem(1),
      overflow: 'hidden',
      position: 'relative',
      transition: 'opacity 0.150s ease-in',
    }),
    buttonContainerHidden: style({
      opacity: 0,
      transition: 'none',
      $nest: {
        '&>*': {
          marginTop: percent(-100),
        },
      },
    }),
    buttonContainerVisible: style({
      opacity: 1,
    }),
    text: style(
      {
        ...centerCenter,
        textAlign: 'center',
        color: colorWhite.toString(),
        lineHeight: em(1.375),
        fontWeight: 200,
        maxWidth: rem(50),
        flexShrink: 0,
        margin: '1rem 0 2rem',
      },
      mediaMobileOnly({
        width: percent(100),
        fontSize: rem(1.125),
        $nest: {
          '& svg': {
            width: 'auto',
            height: '1.5rem',
            marginLeft: rem(0.25),
          },
        },
      }),
      mediaTablet({
        width: viewWidth(69),
        fontSize: rem(1.375),
        $nest: {
          '& svg': {
            width: 'auto',
            height: '2rem',
            marginLeft: rem(0.25),
          },
        },
      }),
    ),
    mandate: style({
      fontSize: rem(0.75),
    }),
    legals: style({
      ...vertical,
      ...verticallySpaced(10),
      ...centerCenter,
      margin: '20px 0 0 0',
      zIndex: 42,
      $nest: {
        '& a': {
          color: colorSubtle.fade(0.75).toString(),
          fontSize: px(14),
          lineHeight: em(1.1),
        },
        '& a:hover': {
          color: colorSubtle.darken(0.1).fade(0.75).toString(),
        },
      },
    }),
    authority: style({
      ...centerCenter,
      ...horizontal,
      ...horizontallySpaced(30),
      margin: '20px 0 0 0',
      zIndex: 42,
      $nest: {
        '&>svg:first-child': {
          height: px(32),
          width: 'auto',
        },
        '& path': {
          fill: colorSlate.toString(),
        },
        '& path:nth-child(3)': {
          fill: colorYouTube.toString(),
        },
        '& path:nth-child(4)': {
          fill: colorWhite.toString(),
        },
      },
    }),
  };
}

const ConnectedChannelReferral = graphql(
  gql`
    query($channel: ID!) {
      channel: getYoutubeChannel(id: $channel) {
        id
        name
        images {
          size
          url
        }
      }
    }
  `,
  {
    options: (variables: Props) => ({
      variables,
      // fetchPolicy: 'network-only'
    }),
  },
)(ChannelReferral);

export default ConnectedChannelReferral;
